import {
  createUrl,
  mapResponseToJson,
  mapResponseToText,
  serializeCookie
} from '@atlassiansox/asc-core';
import { fetch } from 'cross-fetch';

import {
  GetCartRequest,
  GetEmailExistsRequest,
  GetEmailExistsResponse,
  GetProductPricingRequest,
  GetProductPricingResponse,
  GetXsrfTokenRequest,
  PatchUserRequest
} from './hamlet-client.types';
import { Cart, Contact } from './models';

export const DEFAULT_BASE_URL = 'https://hamlet.atlassian.com';

export type HamletClientOptions = {
  readonly baseUrl?: string;
};

export interface IHamletClient {
  getXsrfToken(request: GetXsrfTokenRequest): Promise<string>;
  getEmailExists(
    request: GetEmailExistsRequest
  ): Promise<GetEmailExistsResponse>;
  getCart(request: GetCartRequest): Promise<Cart>;
  patchUser(request: PatchUserRequest): Promise<Contact>;
  getProductPricing(
    request: GetProductPricingRequest
  ): Promise<GetProductPricingResponse>;
}

/**
 * @see: https://hello.atlassian.net/wiki/spaces/HAMS/pages/127995591/Hamlet+API
 */
export class HamletClient implements IHamletClient {
  private readonly baseUrl: string;

  public constructor({ baseUrl = DEFAULT_BASE_URL }: HamletClientOptions = {}) {
    this.baseUrl = baseUrl;
  }

  public async getXsrfToken({
    cloudSessionTokenCookie
  }: GetXsrfTokenRequest): Promise<string> {
    return mapResponseToText(
      await fetch(`${this.baseUrl}/1.0/auth/xsrf/plain`, {
        headers: {
          cookie: serializeCookie(cloudSessionTokenCookie)
        }
      })
    );
  }

  public async getEmailExists({
    email
  }: GetEmailExistsRequest): Promise<GetEmailExistsResponse> {
    return mapResponseToJson(
      await fetch(`${this.baseUrl}/1.0/public/aid/exists/${email}`, {
        method: 'POST'
      })
    );
  }

  public async getCart({ cartId }: GetCartRequest): Promise<Cart> {
    return mapResponseToJson(
      await fetch(`${this.baseUrl}/1.0/public/order/cart/${cartId}`)
    );
  }

  public async patchUser({
    cloudSessionTokenCookie,
    firstName,
    lastName
  }: PatchUserRequest): Promise<Contact> {
    return mapResponseToJson(
      await fetch(`${this.baseUrl}/1.0/public/contact/currentUser/patch`, {
        method: 'PATCH',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'ATL-XSRF-Token': await this.getXsrfToken({
            cloudSessionTokenCookie
          }),
          'Cookie': serializeCookie(cloudSessionTokenCookie)
        },
        body: JSON.stringify([
          {
            op: 'replace',
            path: '/contactDetails/firstName',
            value: firstName
          },
          {
            op: 'replace',
            path: '/contactDetails/lastName',
            value: lastName
          }
        ])
      })
    );
  }

  public async getProductPricing({
    productKeys,
    licenseType = 'evaluation',
    requestingApplication = 'WAC',
    monthsValid = '1'
  }: GetProductPricingRequest): Promise<GetProductPricingResponse> {
    return mapResponseToJson(
      await fetch(
        createUrl({
          baseUrl: this.baseUrl,
          path: `/1.0/public/pricing/query`,
          query: {
            licenseType,
            productKey: productKeys,
            requestingApplication,
            monthsValid,
            cacheBuster: Date.now()
          }
        })
      )
    );
  }
}
